import React from "react";
import { BRAND } from "../lib/brand";
import { Phone, ShieldCheck, Award, Shield, Star } from "lucide-react";

export default function HeroVideo() {
  return (
    <section
      data-testid="hero-section"
      className="relative w-full bg-white overflow-hidden"
    >
      {/* Full-bleed banner container */}
      <div className="relative w-full min-h-[560px] sm:min-h-[640px] md:min-h-[720px] lg:min-h-0 lg:aspect-[22/10]">
        {/* Image */}
        <img
          data-testid="hero-image"
          src={BRAND.heroImage}
          alt="Dead Bug Edwards headquarters - 300 S State Road 7, Plantation, Florida"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "30% center" }}
        />

        {/* Bright, light overlays - keep the building visible while making text readable */}
        <div className="absolute inset-0 bg-gradient-to-l from-white via-white/85 via-42% to-white/5" />
        <div className="absolute inset-0 md:hidden bg-white/70" />

        {/* Since 1959 ribbon */}
        <img
          src={BRAND.since1959Ribbon}
          alt="Since 1959"
          className="absolute top-24 md:top-36 left-4 md:left-8 lg:left-12 w-24 sm:w-32 md:w-52 lg:w-64 drop-shadow-[0_15px_25px_rgba(0,0,0,0.25)]"
          data-testid="hero-since-ribbon"
        />

        {/* Content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-6 flex items-center justify-center md:justify-end pt-[110px] sm:pt-[140px] md:pt-[160px] pb-12 md:pb-16">
          <div className="w-full max-w-2xl text-center md:text-right md:ml-auto">
            <div
              className="reveal inline-flex items-center gap-2 bg-[color:var(--dbe-red)]/10 text-[color:var(--dbe-red)] rounded-full px-3 sm:px-4 py-1.5 text-[10px] sm:text-xs font-bold tracking-[0.2em] sm:tracking-[0.25em] uppercase backdrop-blur-sm"
              data-testid="hero-badge-since"
            >
              <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Since {BRAND.since} · {BRAND.yearsOfService}
            </div>

            <h1
              data-testid="hero-headline"
              className="reveal mt-5 sm:mt-6 text-neutral-900 font-bebas leading-[0.92] text-[clamp(2.5rem,7vw,6rem)]"
              style={{ animationDelay: "0.1s" }}
            >
              Pests out.<br />
              <span className="text-[color:var(--dbe-red)]">Peace in.</span>
            </h1>

            <p
              className="reveal mt-5 sm:mt-6 text-neutral-800 text-base sm:text-lg md:text-xl max-w-xl mx-auto md:ml-auto md:mr-0 leading-relaxed"
              style={{ animationDelay: "0.25s" }}
            >
              South Florida's #1 termite and pest control team - family-rooted,
              science-backed, and still answering the phone since 1959.
            </p>

            <div
              className="reveal mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-end"
              style={{ animationDelay: "0.4s" }}
            >
              <a
                href="#quote"
                data-testid="hero-free-estimate-btn"
                className="group inline-flex items-center justify-center gap-3 bg-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red-deep)] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full font-bold uppercase tracking-wider text-xs sm:text-sm shadow-[0_20px_50px_-15px_rgba(227,30,36,0.55)] transition-transform hover:-translate-y-0.5"
              >
                Get a Free Estimate
                <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href={`tel:${BRAND.phoneTel}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-white hover:bg-neutral-50 border-2 border-neutral-900/10 hover:border-[color:var(--dbe-red)]/40 text-neutral-900 px-6 sm:px-8 py-3.5 sm:py-4 rounded-full font-bebas text-xl sm:text-2xl tracking-wide transition-colors shadow-[0_15px_35px_-15px_rgba(0,0,0,0.2)]"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-[color:var(--dbe-red)]" /> {BRAND.phoneDisplay}
              </a>
            </div>

            <div
              className="reveal mt-7 sm:mt-8 flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 sm:gap-y-3 text-neutral-800 text-xs sm:text-sm justify-center md:justify-end"
              style={{ animationDelay: "0.55s" }}
            >
              <span className="flex items-center gap-2"><Award className="w-4 h-4 text-[color:var(--dbe-red)]" /> Family Owned</span>
              <span className="h-4 w-px bg-neutral-400 hidden sm:block" />
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[color:var(--dbe-red)]" /> Licensed & Insured</span>
              <span className="h-4 w-px bg-neutral-400 hidden sm:block" />
              <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[color:var(--dbe-red)] fill-[color:var(--dbe-red)]" /> 5-Star Reviewed</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
